import { useScienceTutorSection } from './scienceTutorContext';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import {
  CONDENSATION_CASES,
  CONDENSATION_LINK_OPTIONS,
  evaluateCondensation,
  formatFormula,
  SMALL_MOLECULE_OPTIONS,
  type CondensationCase,
  type CondensationLink,
  type CondensationMonomer,
  type CondensationResult,
  type SmallMolecule,
} from './polymersBiomolecules';
import { FeedbackBox, SegmentFormula } from './PolymersShared';
import { CARD_CLASS, chipClass } from './polymersStyles';

function MonomerCard({ monomer }: { monomer: CondensationMonomer }) {
  return (
    <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
      <p className="font-extrabold text-ink">{monomer.name}</p>
      <p className="mt-2 break-words font-mono text-lg font-extrabold text-ink">{monomer.text}</p>
      <p className="mt-2 text-sm font-bold text-slate2">
        {formatFormula(monomer.counts)} · reacts through {monomer.reactingGroup}
      </p>
    </div>
  );
}

function CondensationReveal({ item }: { item: CondensationCase }) {
  return (
    <section className="rounded-[1.75rem] border border-brand-mint/30 bg-wash-mint p-5">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
        Model revealed · {item.polymerClass}
      </p>
      <p className="mt-3 text-sm font-extrabold text-ink">Part of the chain (new links marked)</p>
      <p className="mt-2 rounded-2xl bg-white/80 p-3 text-lg">
        <SegmentFormula segments={item.chainSegments} />
      </p>
      <dl className="mt-4 grid gap-3 text-sm md:grid-cols-2">
        <div className="rounded-2xl bg-white/80 p-3">
          <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Repeat unit</dt>
          <dd className="mt-1 break-words font-mono font-extrabold text-ink">
            {item.repeatUnitText}
          </dd>
        </div>
        <div className="rounded-2xl bg-white/80 p-3">
          <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            Atom balance per repeat unit
          </dt>
          <dd className="mt-1 break-words font-mono font-extrabold text-ink">
            {formatFormula(item.monomerA.counts)} + {formatFormula(item.monomerB.counts)} →{' '}
            {formatFormula(item.repeatUnitCounts)} + {item.waterPerRepeatUnit} H₂O
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-sm font-medium leading-6 text-slate2">
        {item.polymerName} is used for {item.commonUse}. Each repeat unit holds two links, so two
        water molecules leave for every repeat unit formed.
      </p>
      {item.industrialNote ? (
        <p className="mt-3 rounded-2xl border border-brand-sunshine/40 bg-wash-sunshine p-3 text-sm font-bold leading-6 text-ink-soft">
          Industry note: {item.industrialNote}
        </p>
      ) : null}
    </section>
  );
}

function CondensationCaseView({ item }: { item: CondensationCase }) {
  const [link, setLink] = useState<CondensationLink | null>(null);
  const [molecule, setMolecule] = useState<SmallMolecule | null>(null);
  useScienceTutorSection('CondensationCaseView', { link, molecule });
  const [result, setResult] = useState<CondensationResult | null>(null);

  const choose = (next: () => void) => {
    next();
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <section className={CARD_CLASS}>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          2 · Two monomers, two reacting groups each
        </p>
        <div className="mt-4 grid items-center gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <MonomerCard monomer={item.monomerA} />
          <Plus className="mx-auto h-6 w-6 text-brand-coral" aria-hidden="true" />
          <MonomerCard monomer={item.monomerB} />
        </div>
      </section>

      <section className={CARD_CLASS}>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          3 · Predict before you join them
        </p>
        <div className="mt-4 grid gap-5 lg:grid-cols-2">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">Which link forms?</legend>
            <div className="mt-3 grid gap-2">
              {CONDENSATION_LINK_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={link === option.id}
                  onClick={() => choose(() => setLink(option.id))}
                  className={chipClass(link === option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">
              Which small molecule is eliminated?
            </legend>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {SMALL_MOLECULE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={molecule === option}
                  aria-label={`Eliminates ${option}`}
                  onClick={() => choose(() => setMolecule(option))}
                  className={`${chipClass(molecule === option)} font-mono`}
                >
                  {option}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
        <button
          type="button"
          disabled={link === null || molecule === null}
          onClick={() => {
            if (link === null || molecule === null) return;
            setResult(evaluateCondensation(item.id, { link, molecule }));
          }}
          className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Join the monomers
        </button>
        {result?.status === 'unsupported' ? (
          <div className="mt-4">
            <FeedbackBox correct={false}>{result.message}</FeedbackBox>
          </div>
        ) : null}
        {result?.status === 'checked' ? (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <FeedbackBox
              correct={result.linkCorrect}
              title={result.linkCorrect ? 'Link: correct' : 'Link: not yet'}
            >
              {result.linkFeedback}
            </FeedbackBox>
            <FeedbackBox
              correct={result.moleculeCorrect}
              title={result.moleculeCorrect ? 'Small molecule: correct' : 'Small molecule: not yet'}
            >
              {result.moleculeFeedback}
            </FeedbackBox>
          </div>
        ) : null}
      </section>

      {result?.status === 'checked' ? <CondensationReveal item={item} /> : null}
    </div>
  );
}

export function PolymersCondensationMode() {
  const [caseId, setCaseId] = useState(CONDENSATION_CASES[0].id);
  useScienceTutorSection('PolymersCondensationMode', { caseId });
  const item = CONDENSATION_CASES.find((entry) => entry.id === caseId) ?? CONDENSATION_CASES[0];

  return (
    <div className="space-y-6">
      <section className={CARD_CLASS}>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          1 · Choose a condensation polymer
        </p>
        <h2 className="mt-2 text-2xl font-extrabold text-ink">Condensation polymers</h2>
        <div
          className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4"
          role="group"
          aria-label="Condensation polymer case"
        >
          {CONDENSATION_CASES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={caseId === entry.id}
              onClick={() => setCaseId(entry.id)}
              className={chipClass(caseId === entry.id)}
            >
              {entry.polymerName}
            </button>
          ))}
        </div>
      </section>
      <CondensationCaseView key={item.id} item={item} />
    </div>
  );
}
